import React, { useState } from 'react';
import { AlertTriangle, ArrowLeft, Send, Loader2, Globe } from 'lucide-react';
import { useTranslation } from '../../contexts/I18nProvider';
import { TaValidationIssue, CountryCodeOverride } from '../../types/picking';
import { ScanTextInput } from '../common/inputs';

/** Feltnavnet valideringen bruker for manglende landkode (TransportAdminIntegrationService). */
const COUNTRY_CODE_FIELD = 'DelCountryCode';

/** Hurtigvalg. Fritekstfeltet dekker alle andre land. */
const QUICK_PICKS = ['NO', 'SE', 'DK'];

/**
 * TA-forhåndsvalidering ved avslutning (Teamwork 36369121). Delt mellom
 * plukk-direkteflyten (avslutt plukk) og pakkflyten (avslutt forsendelse).
 *
 * Vises når fraktvalideringen fant avvik FØR bookingen gjøres — mens operatøren fortsatt
 * kan gjøre noe med det. Avvikene kommer både fra Compass-side sjekker som gjelder alle
 * TA-er (manglende landkode, NVIT-tolldata — TW 36395424) og fra TA-ens egen validering
 * der den finnes (i dag nShift: overvekt m.m.). Tekstene er derfor TA-nøytrale.
 *
 * To veier videre, bevisst gjort vanskelige å forveksle (store, vertikalt stablede
 * knapper med tydelig avstand og ulik farge — trygt på små terminaler):
 *  - «Avbryt og rett» (primær): tilbake til skjermbildet operatøren sto i.
 *  - «Send likevel»: fullfører som før. Hos nShift sørger SaveShipmentOnError for at
 *    forsendelsen lander i innboksen der manglene kan kompletteres.
 *    Valget logges sporbart (hvem overstyrte hva).
 *
 * Landkode (TW 36376022): når et avvik gjelder manglende landkode kan operatøren sette den her
 * — nShift avviser adresser uten landkode, og kunden kan ikke alltid rette feltet i ERP. Hver
 * ordre får sitt eget valg; et plukk kan inneholde ordrer til ulike land. Verdien sendes FØRST
 * når «Send likevel» trykkes: hurtigvalg og fokusskifte lagrer ingenting, og «Avbryt og rett»
 * lar feltet stå urørt. Feltet er ikke obligatorisk — valideringen er bevisst ikke en sperre.
 */
interface TaValidationDialogProps {
  isOpen: boolean;
  issues: TaValidationIssue[];
  /** «Avbryt og rett» — tilbake til skjermbildet, ingenting fullføres */
  onCancel: () => void;
  /**
   * «Send likevel» — fullfør; forsendelsen kan kompletteres i transportsystemet etterpå.
   * Landkoder operatøren har fylt ut følger med (tom liste når ingen er satt).
   */
  onProceed: (countryCodeOverrides?: CountryCodeOverride[]) => void;
  busy?: boolean;
  /**
   * Lar operatøren sette landkode på landkode-avvik (36376022). Av som standard — kun
   * plukk-flyten er koblet til å sende verdien videre til bookingen.
   */
  allowCountryCodeFix?: boolean;
  /** Feilmelding fra serveren (f.eks. avvist landkode) — vises i dialogen, som forblir åpen. */
  errorMessage?: string | null;
}

export const TaValidationDialog: React.FC<TaValidationDialogProps> = ({
  isOpen,
  issues,
  onCancel,
  onProceed,
  busy = false,
  allowCountryCodeFix = false,
  errorMessage = null
}) => {
  const { t } = useTranslation();
  // Landkode per ordre. Bor kun her til OK trykkes — ingenting lagres underveis.
  const [countryCodes, setCountryCodes] = useState<Record<number, string>>({});
  const [localError, setLocalError] = useState<string | null>(null);

  if (!isOpen) return null;

  const isCountryIssue = (issue: TaValidationIssue) =>
    allowCountryCodeFix && issue.field === COUNTRY_CODE_FIELD && issue.orderNo > 0;

  const setCountryCode = (orderNo: number, value: string) => {
    setLocalError(null);
    setCountryCodes(prev => ({ ...prev, [orderNo]: value }));
  };

  const handleProceed = () => {
    const entered = Object.entries(countryCodes)
      .map(([orderNo, code]) => ({ orderNo: Number(orderNo), countryCode: code.trim() }))
      .filter(o => o.countryCode.length > 0);

    // Formsjekk her gir umiddelbar retting på terminalen; serveren har fasiten (ISO-lista)
    // og avviser koder som «XX» med sin egen melding.
    const malformed = entered.filter(o => !/^[A-Za-z]{2}$/.test(o.countryCode));
    if (malformed.length > 0) {
      setLocalError(t('warehouse.modules.picking.taValidation.countryCodeInvalid', {},
        'Landkoden må være to bokstaver (ISO 3166-1), for eksempel NO, SE eller DE. Landnavn som «Norge» og telefonlandkoder som «47» godtas ikke.'));
      return;
    }

    setLocalError(null);
    onProceed(entered.map(o => ({ orderNo: o.orderNo, countryCode: o.countryCode.toUpperCase() })));
  };

  const shownError = localError || errorMessage;
  const hasEnteredCountryCode = Object.values(countryCodes).some(c => c.trim().length > 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle size={22} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 mb-1">
              {t('warehouse.modules.picking.taValidation.title', {}, 'Forsendelsen passerte ikke fraktvalideringen')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('warehouse.modules.picking.taValidation.prompt', {},
                'Fraktvalideringen fant følgende avvik. Rett dem nå, eller send likevel og fullfør forsendelsen i transportsystemet etterpå.')}
            </p>
          </div>
        </div>

        <ul className="mb-5 space-y-2">
          {issues.map((issue, idx) => (
            <li
              key={idx}
              className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900"
            >
              <div className="flex items-start gap-2">
                <span className="font-medium flex-shrink-0">
                  {issue.orderNo > 0
                    ? t('warehouse.modules.picking.taValidation.orderPrefix', { '0': String(issue.orderNo) }, 'Ordre {0}:')
                    : ''}
                </span>
                <span className="break-words">{issue.message}</span>
              </div>

              {/* Landkode kan settes her (36376022) — én kode per ordre, sendes først ved OK */}
              {isCountryIssue(issue) && (
                <div className="mt-3 pt-3 border-t border-amber-200">
                  <div className="flex items-center gap-2 mb-2 text-amber-900">
                    <Globe size={16} className="flex-shrink-0" />
                    <span className="font-medium">
                      {t('warehouse.modules.picking.taValidation.countryCodeLabel', {}, 'Sett landkode for denne ordren:')}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-2">
                    {QUICK_PICKS.map(code => {
                      const selected = (countryCodes[issue.orderNo] || '').toUpperCase() === code;
                      return (
                        <button
                          key={code}
                          type="button"
                          onClick={() => setCountryCode(issue.orderNo, code)}
                          disabled={busy}
                          className={`px-4 py-2 min-h-[44px] min-w-[60px] rounded-lg border font-semibold transition-colors disabled:opacity-50 ${
                            selected
                              ? 'border-blue-500 bg-blue-600 text-white'
                              : 'border-amber-300 bg-white text-amber-900 hover:bg-amber-100'
                          }`}
                        >
                          {code}
                        </button>
                      );
                    })}
                  </div>

                  <label className="block">
                    <span className="block text-xs text-amber-800 mb-1">
                      {t('warehouse.modules.picking.taValidation.countryCodeOther', {}, 'Annet land (to bokstaver, f.eks. DE, GB):')}
                    </span>
                    <ScanTextInput
                      value={countryCodes[issue.orderNo] || ''}
                      onChange={e => setCountryCode(issue.orderNo, e.target.value)}
                      disabled={busy}
                      maxLength={2}
                      placeholder={t('warehouse.modules.picking.taValidation.countryCodePlaceholder', {}, 'f.eks. DE')}
                      className="w-full px-3 py-2 min-h-[44px] border border-amber-300 rounded-lg bg-white text-gray-900 uppercase tracking-widest font-mono disabled:opacity-50"
                    />
                  </label>
                </div>
              )}
            </li>
          ))}
        </ul>

        {shownError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 break-words">
            {shownError}
          </div>
        )}

        {/* Bevisst vertikal stabel med stor avstand — de to valgene skal ikke kunne
            forveksles på et nettbrett. Primærvalget (trygt) øverst og mest fremtredende. */}
        <div className="flex flex-col gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 min-h-[52px] bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
          >
            <ArrowLeft size={18} />
            {t('warehouse.modules.picking.taValidation.cancelAndFix', {}, 'Avbryt og rett')}
          </button>
          <button
            onClick={handleProceed}
            disabled={busy}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 min-h-[52px] border-2 border-amber-400 bg-white text-amber-800 rounded-lg font-medium hover:bg-amber-50 disabled:opacity-50"
          >
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {/* Etiketten skal si det den gjør: med landkode utfylt rettes avviket her, og
                da er «send likevel» feil beskjed. */}
            {hasEnteredCountryCode
              ? t('warehouse.modules.picking.taValidation.proceedWithCountryCode', {}, 'Send med landkoden over')
              : t('warehouse.modules.picking.taValidation.proceedAnyway', {}, 'Send likevel')}
          </button>
        </div>
      </div>
    </div>
  );
};
